import { dateCompare, DateFormatHelper, DetailItem, DetailSection, DOMAIN_TYPES, LeaseEnums, RecordMap } from "@/Shared";
import { LeaseModel } from "../LeaseModel";
import { AuditViewModel } from "@/Shared/Logic/ViewModels/AuditViewModel";
import { AuditModel } from "@/Shared/Logic/Models/AuditModel";
import { TransactionViewModel } from "@/Domains/Transaction/Logic/ViewModels/TransactionViewModel";
import { DocumentModel } from "@/Domains/Document";
import { LeaseTenantLinkModel } from "../LeaseTenantLinkModel";
import { AccessRightModel } from "@/Domains/Auth";



export interface LeaseVMParams {
    lease: LeaseModel;
    viewEnums: LeaseEnums;
    documentMap: RecordMap<DocumentModel>;
    isDisplaying : boolean;
    accessRight :AccessRightModel
}

export class LeaseViewModel {

    public model! : LeaseModel;
    public id: number;
    public leaseType!: string;
    public leaseStart!: string;
    public leaseEnd!: string;

    public revisionRate!: number;
    public revisionFrequency!: string;
    public transactionId!: number
    
    
    public tenants!: LeaseTenantLinkModel[];
    public audit!: AuditViewModel;

    public activity! : string;
    public usageDescription! : string;

    private transactionVM?: TransactionViewModel;

    private _documentMap? : RecordMap<DocumentModel>;

    public cardHeaderTitle!: string;
    public cardClass!: string;
    public isDeleted!: boolean;
    

    public constructor(private params: LeaseVMParams) {

        const { lease, viewEnums, documentMap, isDisplaying, accessRight } = params;
        
        this.model = lease;
        this.isDeleted = !!lease.deletedAt;
        this._documentMap = documentMap;
        this.id = lease.id;
        this.leaseType = viewEnums.LeaseTypeEnum.find(t => t.value === lease.leaseType)?.label ?? "";
        this.leaseStart = DateFormatHelper.toLocaleDateString(lease.leaseStart);
        this.leaseEnd = DateFormatHelper.toLocaleDateString(lease.leaseEnd);
        this.tenants = lease.tenants ?? [];

        this.revisionRate = lease.revisionRate ?? 0;
        this.revisionFrequency = lease.revisionFrequency ?? "";
        this.transactionId = lease.transactionId ?? 0;

        this.activity = lease.activity ?? "";
        this.usageDescription = lease.usageDescription ?? "";

        this.audit = new AuditViewModel(lease as AuditModel);

        if (lease.transaction) {
            this.transactionVM = new TransactionViewModel({
                transaction :lease.transaction, 
                viewEnums : viewEnums.TransactionEnums,
                isDisplaying : false,
                accessRight
            });
        }

        this.cardHeaderTitle = `${this.leaseType} - ID : ${this.id}`;
        this.cardClass = isDisplaying ? 'active' : '';
    }

    public setDocumentList(documentMap : RecordMap<DocumentModel>) {
        this._documentMap = documentMap;
    }

    hasDocument(): boolean {

        return this._documentMap ? !this._documentMap.isEmpty() : false;
    }

    getLatestDocument() {
        return this._documentMap?.lastById();
    }

    documentMap() : RecordMap<DocumentModel>|undefined {
        return this._documentMap;
    }


    getAllSections = (): DetailSection[] => {
        const sections: DetailSection[] = [
            {
                sectionFn: this.getLeaseSection,
                legend: 'Informations Générale',
                type: 'box'
            },
            
            {
                sectionFn : this.getMoreSection,
                legend: 'Détails du bail',
                type: 'box'
            },
            {
                sectionFn: this.getTenantsSection,
                legend: 'Locataires',
                type: 'box'
            },
            {
                sectionFn: this.getOptionsSection,
                legend: 'Options',
                type: 'box'
            },

        ];

        if (this.transactionVM) {
            sections.push({
                sectionFn: this.transactionVM.getTransactionSection,
                legend: 'Transaction',
                type: 'box'
            });
        }

        if(this.params.accessRight.canAudit(DOMAIN_TYPES.Lease)) {
            sections.push({
                sectionFn: this.audit.getAuditTrail,
                legend: 'Traçabilité',
                type: 'box'
            });
        }

        return sections;
    }

    getLeaseSection = () => {
        return [
            { label: "N° Bail", value: this.id },
            { label : "Type ", value : this.getLeaseTypeLabel()},
            { label: "Début", value: this.leaseStart },
            { label: "Fin", value: this.leaseEnd },
            
        ] as DetailItem[];
    }

    getMoreSection = () => {
        return [
            { label: "Taux Révision (%)", value: this.revisionRate },
            { label: "Fréquence", value: this.revisionFrequency },
            { label: "Activité", value: this.activity },
            { label: "Usage", value: this.usageDescription },
        ] as DetailItem[];
    }

    getTenantsSection = () => {
        return this.tenants.map((tenantLink, index) => {
            const tenantPerson = tenantLink.tenant?.person;
            return { 
                label: `Locataire ${index + 1}`, 
                value: `${tenantPerson?.lastname?.toUpperCase() ?? ""} ${tenantPerson?.firstname ?? ""}`
            }
        }) as DetailItem[];
    }

    private getLeaseTypeLabel = () => {
         return this.params.viewEnums.LeaseTypeEnum.find(type => type.value === this.leaseType)?.label ?? "";
    }

    getOptionsSection = () => {
       return this.params.viewEnums.LeaseOptionEnum.map(opt => {
          return { label: opt.label, value: this.model.leaseOptions?.includes(opt.value as string) ? "Oui" : "Non" }
       }) as DetailItem[];
    }

    get title() {
        return "Bail N° " + this.id + " - " + this.getLeaseTypeLabel().toUpperCase();
    }

    compareTo(other: LeaseViewModel): number {
        return dateCompare(this.model.createdAt ?? "", other.model.createdAt ?? "");
    }
}
